/**
 * RoadDashboardPage — J6 (PRD §12.3.9 frontend page #3 + §9.14 Road Metaphor)
 *
 * The Road metaphor (v1.1.18 layout):
 *   Row 1: Vision · Core Values · Company Principles  (the destination + rules)
 *   Row 2: Active Domains · Open Work Items · Verification Inbox  (lanes + flow)
 *   Below: OS Health Signals (dashboard tiles)
 *
 * v1.1.18 — removed the Vision Goals card; reordered cards into a strategy
 * row (top) + a flow row (bottom); added the Verification Inbox card mirroring
 * the Open Work Items card layout.
 *
 * Adds two export buttons (PDF, Markdown) that hit the Road dashboard export
 * endpoint and trigger a browser download via Blob.
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Compass,
  Heart,
  Scale,
  Briefcase,
  ListChecks,
  CheckSquare,
  FileDown,
  FileText,
  AlertCircle,
} from 'lucide-react';
import { RorosPage } from '@/components/roros/RorosPage';
import { Button } from '@/components/ui/Button';
import { Alert } from '@/components/ui/Alert';
// D.3 / CLEAN-J6-A3: SectionCard extracted to shared DashboardSection component.
import { DashboardSection } from '@/components/roros/dashboards/DashboardSection';
import { OsHealthTiles } from '@/components/roros/observability/OsHealthTiles';
import { showToast } from '@/components/ui/Toast';
import { apiFetch } from '@/lib/api';
import { t } from '@/lib/i18n';
import { useRorosHealth } from '@/hooks/roros/observability';
import { useRorosDomains } from '@/hooks/roros/domains';
import { useRorosCompanyProfile, useRorosCoreValues, useRorosCompanyPrinciples } from '@/hooks/roros/overarching';
import { useRorosControlTowerOverview, useRorosVerificationInbox } from '@/hooks/roros/control-tower';
import type { RorosHealthSignalDTO } from '@/services/roros/control-tower.service';
import type { RorosDomainDTO } from '@/types/roros/domains';
import type { RorosCoreValueDTO, RorosCompanyPrincipleDTO } from '@/types/roros/overarching';

// ─── Main page ──────────────────────────────────────────────────────────────

export function RoadDashboardPage() {
  const healthQ = useRorosHealth();
  const domainsQ = useRorosDomains();
  const profileQ = useRorosCompanyProfile();
  const coreValuesQ = useRorosCoreValues();
  const principlesQ = useRorosCompanyPrinciples();
  const overviewQ = useRorosControlTowerOverview();
  const inboxQ = useRorosVerificationInbox();

  const signals = (healthQ.data ?? []) as RorosHealthSignalDTO[];
  const domains = (domainsQ.data ?? []) as RorosDomainDTO[];
  const activeDomains = domains.filter((d) => d.isActive);
  // F9 dogfood-fix-wave-1: server response is { profile: RorosCompanyProfileDTO|null, completion: ... }
  const profile = profileQ.data?.profile ?? null;

  const coreValues = ((coreValuesQ.data ?? []) as unknown) as RorosCoreValueDTO[];
  const principles = ((principlesQ.data ?? []) as unknown) as RorosCompanyPrincipleDTO[];
  const overview = overviewQ.data as { statusCounts?: Record<string, number> } | undefined;
  const sc = overview?.statusCounts ?? {};
  const openWorkItems =
    (sc.ready ?? 0) +
    (sc.in_progress ?? 0) +
    (sc.quality_confirmation ?? 0) +
    (sc.blocked ?? 0) +
    (sc.escalated ?? 0);
  const inboxCount = inboxQ.data?.meta?.total ?? 0;

  // ─── Export handlers ───────────────────────────────────────────────────────
  const [exporting, setExporting] = useState<'md' | 'pdf' | null>(null);

  async function downloadExport(format: 'md' | 'pdf') {
    setExporting(format);
    try {
      const res = await apiFetch(`/api/v1/observability/road-dashboard/export?format=${format}`);
      if (!res.ok) {
        const errBody = await res.json().catch(() => null);
        throw new Error(errBody?.error?.message ?? `Export failed (${res.status})`);
      }
      const blob = await res.blob();
      // Browsers won't honour Content-Disposition on a fetch — we drive the
      // download via an anchor click against the blob URL.
      const stamp = new Date().toISOString().slice(0, 10);
      const filename = `road-dashboard-${stamp}.${format}`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      showToast.success(t('Road dashboard exported'));
    } catch (err: any) {
      showToast.error(err?.message ?? t('Export failed'));
    } finally {
      setExporting(null);
    }
  }

  return (
    <RorosPage
      title={t('Road Dashboard')}
      icon={<Compass className="w-6 h-6" />}
      breadcrumbs={[{ label: t('Insights'), path: '/roros/observability' }]}
      actions={
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            isLoading={exporting === 'md'}
            onClick={() => downloadExport('md')}
            data-testid="road-export-md"
          >
            <FileText className="w-4 h-4 mr-1.5" aria-hidden />
            {t('Download Markdown')}
          </Button>
          <Button
            variant="primary"
            size="sm"
            isLoading={exporting === 'pdf'}
            onClick={() => downloadExport('pdf')}
            data-testid="road-export-pdf"
          >
            <FileDown className="w-4 h-4 mr-1.5" aria-hidden />
            {t('Download PDF')}
          </Button>
        </div>
      }
    >
      {(healthQ.isError || domainsQ.isError) && (
        <Alert variant="error" title={t('Error loading dashboard')} className="mb-4">
          <div className="flex items-start gap-2">
            <AlertCircle className="h-4 w-4 mt-0.5" aria-hidden />
            <span>{healthQ.error?.message ?? domainsQ.error?.message ?? ''}</span>
          </div>
        </Alert>
      )}

      {/* Row 1 — strategy: Vision · Core Values · Company Principles */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <DashboardSection
          icon={<Compass className="w-5 h-5" />}
          title={t('Vision')}
          linkTo="/roros/company-profile?tab=company"
          testId="road-section-vision"
        >
          {profile?.vision ? (
            <p className="text-sm text-foreground leading-relaxed">{profile.vision}</p>
          ) : (
            <p className="text-sm text-muted-foreground italic">{t('No vision recorded yet.')}</p>
          )}
        </DashboardSection>

        <DashboardSection
          icon={<Heart className="w-5 h-5" />}
          title={t('Core Values')}
          count={coreValues.length}
          linkTo="/roros/company-profile?tab=company"
          testId="road-section-core-values"
        >
          {coreValues.length === 0 ? (
            <p className="text-sm text-muted-foreground italic">{t('No core values defined.')}</p>
          ) : (
            <ul className="space-y-1.5 text-sm">
              {coreValues.slice(0, 6).map((v) => (
                <li key={v.id} className="text-foreground">• {v.title}</li>
              ))}
            </ul>
          )}
        </DashboardSection>

        <DashboardSection
          icon={<Scale className="w-5 h-5" />}
          title={t('Company Principles')}
          count={principles.length}
          linkTo="/roros/company-profile?tab=company"
          testId="road-section-principles"
        >
          {principles.length === 0 ? (
            <p className="text-sm text-muted-foreground italic">{t('No principles defined.')}</p>
          ) : (
            <ul className="space-y-1.5 text-sm">
              {principles.slice(0, 6).map((p) => (
                <li key={p.id} className="text-foreground">• {p.title}</li>
              ))}
            </ul>
          )}
        </DashboardSection>
      </div>

      {/* Row 2 — flow: Active Domains · Open Work Items · Verification Inbox */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <DashboardSection
          icon={<Briefcase className="w-5 h-5" />}
          title={t('Active Domains')}
          count={activeDomains.length}
          linkTo="/roros/domains"
          testId="road-section-active-domains"
        >
          {activeDomains.length === 0 ? (
            <p className="text-sm text-muted-foreground italic">{t('No active domains.')}</p>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {activeDomains.map((d) => (
                <Link
                  key={d.id}
                  to={`/roros/domains/${d.id}`}
                  className="text-xs px-2 py-1 rounded-md bg-primary-50 text-primary-700 hover:bg-primary-100"
                >
                  {d.name}
                </Link>
              ))}
            </div>
          )}
        </DashboardSection>

        <DashboardSection
          icon={<ListChecks className="w-5 h-5" />}
          title={t('Open Work Items')}
          count={openWorkItems}
          linkTo="/roros/control-tower"
          testId="road-section-open-work"
        >
          <p className="text-3xl font-semibold text-foreground">{openWorkItems}</p>
          <p className="text-xs text-muted-foreground">
            {t('Total work in flight (ready, in progress, blocked, escalated).')}
          </p>
        </DashboardSection>

        <DashboardSection
          icon={<CheckSquare className="w-5 h-5" />}
          title={t('Verification Inbox')}
          count={inboxCount}
          linkTo="/roros/verification-inbox"
          testId="road-section-verification-inbox"
        >
          <p className="text-3xl font-semibold text-foreground">{inboxCount}</p>
          <p className="text-xs text-muted-foreground">{t('Items to be verified')}</p>
        </DashboardSection>
      </div>

      {/* OS Health 14 tiles */}
      <OsHealthTiles signals={signals} title={t('OS Health Signals')} />
    </RorosPage>
  );
}
